import React from 'react';
import { Formik, Form, Field } from 'formik';
import { FormikTextInputV3 } from 'Components/fields/TextInput';
import {
  Button,
  Card,
  Divider,
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'pounceV2';
import * as Yup from 'yup';
import { Editor } from '@tiptap/react';
import { useTheme } from '@mui/material';

export interface LinkFormValues {
  text: string;
  url: string;
}

const validationSchema: Yup.SchemaOf<LinkFormValues> = Yup.object().shape({
  text: Yup.string().required(),
  url: Yup.string().url().nullable(),
});

const RichTextEditorControlsV3: React.FC<{ editor: Editor }> = ({ editor }) => {
  const [initialValues, setInitialValues] = React.useState({
    text: '',
    url: '',
  });
  const theme = useTheme();
  const popLinkModal = () => {
    const isEditingLink = editor.isActive('link');

    // If cursor lies within an existing link's text, select the entire text of that link
    if (isEditingLink) {
      editor.commands.extendMarkRange('link');
    }

    const { view, state } = editor;
    const { from, to } = view.state.selection;
    const selectedText = state.doc.textBetween(from, to, '');
    let selectedUrl = editor.getAttributes('link').href || '';

    if (!isEditingLink) {
      selectedUrl = '';
    }

    setInitialValues({
      text: selectedText,
      url: selectedUrl,
    });
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  const saveLink = values => {
    // If editing an existing link, select its text
    editor.commands.extendMarkRange('link');

    const { view } = editor;
    const { anchor } = view.state.selection;
    const { text, url } = values;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }

    // Update link's text in case it was changed
    editor
      .chain()
      .focus()
      .deleteSelection()
      .insertContentAt(anchor, text, { updateSelection: true })
      .run();
  };

  return (
    <Flex borderBottom="1px solid" borderBottomColor={theme.palette.tokens.stroke.neutral[4]} p={2}>
      <IconButton
        aria-label="bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        size="small"
        icon="bold"
        variant="ghost"
        active={editor?.isActive('bold')}
      />

      <IconButton
        aria-label="italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        size="small"
        icon="italic"
        variant="ghost"
        active={editor?.isActive('italic')}
      />

      <IconButton
        aria-label="strike"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        size="small"
        icon="strikethrough"
        variant="ghost"
        active={editor?.isActive('strike')}
      />

      <Divider orientation="vertical" color={theme.palette.tokens.stroke.neutral[5]} />

      <Popover>
        {({ close }) => (
          <>
            <PopoverTrigger
              as={IconButton}
              active={editor?.isActive('link')}
              aria-label="link"
              icon="link"
              size="small"
              variant="ghost"
              onClick={popLinkModal}
            />
            <PopoverContent alignment="bottom-center">
              <Card p={6} maxWidth="50vw" minWidth="350px">
                <Formik<LinkFormValues>
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={values => {
                    saveLink(values);
                    close();
                  }}
                >
                  <Form>
                    <Flex direction="column" gap={2}>
                      <Field
                        as={FormikTextInputV3}
                        label="Text"
                        name="text"
                        placeholder="Display text..."
                      />

                      <Field
                        as={FormikTextInputV3}
                        label="URL"
                        name="url"
                        placeholder="https://example.com"
                      />

                      <Flex direction="row" gap={2} justify="space-between">
                        <Button onClick={close} size="small" variant="secondary">
                          Cancel
                        </Button>
                        <Flex gap={2}>
                          {initialValues.url && editor.isActive('link') && (
                            <Button
                              onClick={() => {
                                removeLink();
                                close();
                              }}
                              size="small"
                              variant="secondary"
                            >
                              Remove
                            </Button>
                          )}
                          <Button type="submit" size="medium">
                            Save
                          </Button>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Form>
                </Formik>
              </Card>
            </PopoverContent>
          </>
        )}
      </Popover>

      <Divider orientation="vertical" color={theme.palette.tokens.stroke.neutral[5]} />

      <IconButton
        aria-label="bullet list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        size="small"
        variant="ghost"
        icon="unordered-list"
        active={editor?.isActive('bulletList')}
      />
      <IconButton
        aria-label="ordered list"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        size="small"
        variant="ghost"
        icon="ordered-list"
        active={editor?.isActive('orderedList')}
      />

      <Divider orientation="vertical" color={theme.palette.tokens.stroke.neutral[5]} />

      <IconButton
        aria-label="blockquote"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        size="small"
        variant="ghost"
        icon="indent"
        active={editor?.isActive('blockquote')}
      />

      <Divider orientation="vertical" color={theme.palette.tokens.stroke.neutral[5]} />

      <IconButton
        aria-label="code"
        onClick={() => editor.chain().focus().toggleCode().run()}
        size="small"
        variant="ghost"
        icon="markup"
        active={editor?.isActive('code')}
      />
    </Flex>
  );
};

export default RichTextEditorControlsV3;
